import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { User, Workspace, Client, Invoice, WorkspaceMember, WorkspaceInvite, WorkspaceSettings } from '../types';
import { generateInvoicePDF } from '../utils/pdfGenerator';

// User operations
export const createUser = async (userId: string, userData: Omit<User, 'id' | 'createdAt'>) => {
  const user = {
    ...userData,
    createdAt: Timestamp.now(),
    lastLogin: Timestamp.now(),
  };
  await setDoc(doc(db, 'users', userId), user);
  return { id: userId, ...user };
};

export const getUser = async (userId: string): Promise<User | null> => {
  const docRef = doc(db, 'users', userId);
  const docSnap = await getDoc(docRef);
  
  if (docSnap.exists()) {
    const data = docSnap.data();
    return {
      id: docSnap.id,
      ...data,
      createdAt: data.createdAt?.toDate?.()?.toISOString?.() || data.createdAt,
      lastLogin: data.lastLogin?.toDate?.()?.toISOString?.() || data.lastLogin,
    } as User;
  }
  return null;
};

export const updateUser = async (userId: string, userData: Partial<User>) => {
  const docRef = doc(db, 'users', userId);
  await updateDoc(docRef, userData);
};

// Workspace operations
export const createWorkspace = async (workspaceData: Omit<Workspace, 'id' | 'createdAt'>) => {
  const now = Timestamp.now();
  const workspace = {
    ...workspaceData,
    createdAt: now,
  };
  const docRef = await addDoc(collection(db, 'workspaces'), workspace);
  return {
    id: docRef.id,
    ...workspaceData,
    createdAt: now.toDate().toISOString()
  };
};

export const getUserWorkspaces = async (userId: string): Promise<Workspace[]> => {
  const q = query(
    collection(db, 'workspaces'),
    where('ownerId', '==', userId)
  );
  const querySnapshot = await getDocs(q);
  
  const workspaces: Workspace[] = [];
  querySnapshot.forEach((doc) => {
    const data = doc.data();
    workspaces.push({
      id: doc.id,
      ...data,
      createdAt: data.createdAt?.toDate?.()?.toISOString?.() || data.createdAt,
    } as Workspace);
  });
  
  return workspaces;
};

export const updateWorkspace = async (workspaceId: string, workspaceData: Partial<Workspace>) => {
  const docRef = doc(db, 'workspaces', workspaceId);
  await updateDoc(docRef, workspaceData);
};

// Update only email settings
export const updateWorkspaceEmailSettings = async (workspaceId: string, emailSettings: Partial<WorkspaceSettings>) => {
  const workspaceRef = doc(db, 'workspaces', workspaceId);
  await updateDoc(workspaceRef, {
    'settings.emailProvider': emailSettings.emailProvider,
    'settings.resendApiKey': emailSettings.resendApiKey,
    'settings.fromEmail': emailSettings.fromEmail,
    'settings.fromName': emailSettings.fromName,
    'settings.smtpHost': emailSettings.smtpHost,
    'settings.smtpPort': emailSettings.smtpPort,
    'settings.smtpUsername': emailSettings.smtpUsername,
    'settings.smtpPassword': emailSettings.smtpPassword,
    'settings.smtpSecure': emailSettings.smtpSecure,
    'settings.updatedAt': serverTimestamp()
  });
};

// Update the whole settings object
export const updateWorkspaceSettings = async (workspaceId: string, settings: Partial<WorkspaceSettings>) => {
  const workspaceRef = doc(db, 'workspaces', workspaceId);
  await updateDoc(workspaceRef, {
    settings: {
      ...settings,
      updatedAt: serverTimestamp()
    }
  });
};

// Workspace member operations
export const inviteWorkspaceMember = async (workspaceId: string, email: string, role: 'admin' | 'member'): Promise<WorkspaceInvite> => {
  const now = Timestamp.now();
  const invite = {
    workspaceId,
    email,
    role,
    status: 'pending' as const,
    createdAt: now,
  };
  const docRef = await addDoc(collection(db, 'workspace_invites'), invite);
  return { 
    id: docRef.id,
    ...invite,
    createdAt: now.toDate().toISOString()
  };
};

export const getWorkspaceMembers = async (workspaceId: string): Promise<WorkspaceMember[]> => {
  const q = query(
    collection(db, 'workspace_members'),
    where('workspaceId', '==', workspaceId)
  );
  const querySnapshot = await getDocs(q);
  
  const members: WorkspaceMember[] = [];
  querySnapshot.forEach((doc) => {
    const data = doc.data();
    members.push({
      id: doc.id,
      ...data,
      joinedAt: data.joinedAt?.toDate?.()?.toISOString?.() || data.joinedAt,
    } as WorkspaceMember);
  });
  
  return members;
};

export const getWorkspaceInvites = async (workspaceId: string): Promise<WorkspaceInvite[]> => {
  const q = query(
    collection(db, 'workspace_invites'),
    where('workspaceId', '==', workspaceId),
    where('status', '==', 'pending')
  );
  const querySnapshot = await getDocs(q);
  
  const invites: WorkspaceInvite[] = [];
  querySnapshot.forEach((doc) => {
    const data = doc.data();
    invites.push({
      id: doc.id,
      ...data,
      createdAt: data.createdAt?.toDate?.()?.toISOString?.() || data.createdAt,
    } as WorkspaceInvite);
  });
  
  return invites;
};

export const updateWorkspaceMemberRole = async (memberId: string, role: WorkspaceMember['role']) => {
  const docRef = doc(db, 'workspace_members', memberId);
  await updateDoc(docRef, { role });
};

export const removeWorkspaceMember = async (memberId: string) => {
  const docRef = doc(db, 'workspace_members', memberId);
  await deleteDoc(docRef);
};

export const cancelWorkspaceInvite = async (inviteId: string) => {
  const docRef = doc(db, 'workspace_invites', inviteId);
  await deleteDoc(docRef);
};

export const subscribeToWorkspaceMembers = (
  workspaceId: string,
  callback: (members: WorkspaceMember[]) => void
) => {
  const q = query(
    collection(db, 'workspace_members'),
    where('workspaceId', '==', workspaceId)
  );
  
  return onSnapshot(q, (querySnapshot) => {
    const members: WorkspaceMember[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      members.push({
        id: doc.id,
        ...data,
        joinedAt: data.joinedAt?.toDate?.()?.toISOString?.() || data.joinedAt,
      } as WorkspaceMember);
    });
    callback(members);
  });
};

export const subscribeToWorkspaceInvites = (
  workspaceId: string,
  callback: (invites: WorkspaceInvite[]) => void
) => {
  const q = query(
    collection(db, 'workspace_invites'),
    where('workspaceId', '==', workspaceId),
    where('status', '==', 'pending')
  );
  
  return onSnapshot(q, (querySnapshot) => {
    const invites: WorkspaceInvite[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      invites.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate?.()?.toISOString?.() || data.createdAt,
      } as WorkspaceInvite);
    });
    callback(invites);
  });
};

// Client operations
export const createClient = async (clientData: Omit<Client, 'id' | 'createdAt'>) => {
  const client = {
    ...clientData,
    createdAt: Timestamp.now(),
  };
  const docRef = await addDoc(collection(db, 'clients'), client);
  return { id: docRef.id, ...client };
};

export const getWorkspaceClients = async (workspaceId: string): Promise<Client[]> => {
  const q = query(
    collection(db, 'clients'),
    where('workspaceId', '==', workspaceId),
    orderBy('createdAt', 'desc')
  );
  const querySnapshot = await getDocs(q);
  
  const clients: Client[] = [];
  querySnapshot.forEach((doc) => {
    const data = doc.data();
    clients.push({
      id: doc.id,
      ...data,
      createdAt: data.createdAt?.toDate?.()?.toISOString?.() || data.createdAt,
    } as Client);
  });
  
  return clients;
};

export const updateClient = async (clientId: string, clientData: Partial<Client>) => {
  const docRef = doc(db, 'clients', clientId);
  await updateDoc(docRef, clientData);
};

export const deleteClient = async (clientId: string) => {
  const docRef = doc(db, 'clients', clientId);
  await deleteDoc(docRef);
};

// Invoice operations
function toInvoiceForPDF(invoice: any): Invoice {
  return {
    ...invoice,
    createdAt: invoice.createdAt?.toDate?.()?.toISOString?.() || invoice.createdAt || new Date().toISOString(),
    updatedAt: invoice.updatedAt?.toDate?.()?.toISOString?.() || invoice.updatedAt || new Date().toISOString(),
    workspaceId: invoice.workspaceId || '',
    clientId: invoice.clientId || invoice.client?.id || '',
    invoiceNumber: invoice.invoiceNumber || '',
    issueDate: invoice.issueDate || new Date().toISOString(),
    dueDate: invoice.dueDate || new Date().toISOString(),
    status: invoice.status || 'draft',
    items: invoice.items || [],
    subtotal: invoice.subtotal || 0,
    taxRate: invoice.taxRate || 0,
    taxAmount: invoice.taxAmount || 0,
    discountAmount: invoice.discountAmount || 0,
    total: invoice.total || 0,
    paymentStatus: invoice.paymentStatus || 'pending',
    paymentLink: invoice.paymentLink,
    notes: invoice.notes,
    terms: invoice.terms,
    paidAt: invoice.paidAt,
    sentAt: invoice.sentAt,
    viewedAt: invoice.viewedAt,
    client: invoice.client,
    downloadUrl: invoice.downloadUrl,
  };
}

export const createInvoice = async (invoiceData: Omit<Invoice, 'id' | 'createdAt' | 'updatedAt'>, workspace?: Workspace) => {
  const invoice = {
    ...invoiceData,
    subtotal: parseFloat(Number(invoiceData.subtotal).toFixed(2)),
    taxAmount: parseFloat(Number(invoiceData.taxAmount).toFixed(2)),
    discountAmount: parseFloat(Number(invoiceData.discountAmount).toFixed(2)),
    total: parseFloat(Number(invoiceData.total).toFixed(2)),
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  };
  const docRef = await addDoc(collection(db, 'invoices'), invoice);
  // If workspace is provided, generate PDF and save downloadUrl
  if (workspace) {
    try {
      const invoiceForPDF = toInvoiceForPDF({ ...invoice, id: docRef.id });
      const downloadUrl = await generateInvoicePDF(invoiceForPDF, workspace);
      await updateDoc(docRef, { downloadUrl });
    } catch (err) {
      console.error('Failed to generate/save invoice PDF:', err);
    }
  }
  return { id: docRef.id, ...invoice };
};

export const getWorkspaceInvoices = async (workspaceId: string): Promise<Invoice[]> => {
  const q = query(
    collection(db, 'invoices'),
    where('workspaceId', '==', workspaceId),
    orderBy('createdAt', 'desc')
  );
  const querySnapshot = await getDocs(q);
  
  const invoices: Invoice[] = [];
  querySnapshot.forEach((doc) => {
    const data = doc.data();
    invoices.push({
      id: doc.id,
      ...data,
      createdAt: data.createdAt?.toDate?.()?.toISOString?.() || data.createdAt,
      updatedAt: data.updatedAt?.toDate?.()?.toISOString?.() || data.updatedAt,
      paidAt: data.paidAt?.toDate?.()?.toISOString?.() || data.paidAt,
      sentAt: data.sentAt?.toDate?.()?.toISOString?.() || data.sentAt,
      viewedAt: data.viewedAt?.toDate?.()?.toISOString?.() || data.viewedAt,
    } as Invoice);
  });
  
  return invoices;
};

export const updateInvoice = async (invoiceId: string, invoiceData: Partial<Invoice>, workspace?: Workspace) => {
  const docRef = doc(db, 'invoices', invoiceId);
  const updateData = {
    ...invoiceData,
    subtotal: invoiceData.subtotal !== undefined ? parseFloat(Number(invoiceData.subtotal).toFixed(2)) : undefined,
    taxAmount: invoiceData.taxAmount !== undefined ? parseFloat(Number(invoiceData.taxAmount).toFixed(2)) : undefined,
    discountAmount: invoiceData.discountAmount !== undefined ? parseFloat(Number(invoiceData.discountAmount).toFixed(2)) : undefined,
    total: invoiceData.total !== undefined ? parseFloat(Number(invoiceData.total).toFixed(2)) : undefined,
    updatedAt: Timestamp.now(),
  };
  await updateDoc(docRef, updateData);
  // If workspace is provided, generate PDF and save downloadUrl
  if (workspace) {
    try {
      const invoiceForPDF = toInvoiceForPDF({ ...updateData, id: invoiceId });
      const downloadUrl = await generateInvoicePDF(invoiceForPDF, workspace);
      await updateDoc(docRef, { downloadUrl });
    } catch (err) {
      console.error('Failed to generate/save invoice PDF:', err);
    }
  }
};

export const deleteInvoice = async (invoiceId: string) => {
  const docRef = doc(db, 'invoices', invoiceId);
  await deleteDoc(docRef);
};

// Real-time listeners
export const subscribeToWorkspaceClients = (
  workspaceId: string,
  callback: (clients: Client[]) => void
) => {
  const q = query(
    collection(db, 'clients'),
    where('workspaceId', '==', workspaceId),
    orderBy('createdAt', 'desc')
  );
  
  return onSnapshot(q, (querySnapshot) => {
    const clients: Client[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      clients.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate?.()?.toISOString?.() || data.createdAt,
      } as Client);
    });
    callback(clients);
  });
};

export const subscribeToWorkspaceInvoices = (
  workspaceId: string,
  callback: (invoices: Invoice[]) => void
) => {
  const q = query(
    collection(db, 'invoices'),
    where('workspaceId', '==', workspaceId),
    orderBy('createdAt', 'desc')
  );
  
  return onSnapshot(q, (querySnapshot) => {
    const invoices: Invoice[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      invoices.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate?.()?.toISOString?.() || data.createdAt,
        updatedAt: data.updatedAt?.toDate?.()?.toISOString?.() || data.updatedAt,
        paidAt: data.paidAt?.toDate?.()?.toISOString?.() || data.paidAt,
        sentAt: data.sentAt?.toDate?.()?.toISOString?.() || data.sentAt,
        viewedAt: data.viewedAt?.toDate?.()?.toISOString?.() || data.viewedAt,
      } as Invoice);
    });
    callback(invoices);
  });
}; 